import {
  type Box,
  type Point,
  type TraceLayout,
  advanceAlong,
  pointAt,
  strokeLength,
  traceScrollOffset,
} from "@ai-teach/canvas-core";
import { useEffect, useRef, useState } from "react";
import { layoutWord } from "./handWritingLayout";

/** How far off the skeleton a finger may wander and still count as on-path. */
const TOLERANCE_EM = 0.3;
const MIN_TOLERANCE = 18;
/** Ceiling on what a single pointer move may advance, so a straight swipe can't shortcut a curve. */
const WINDOW_EM = 0.55;
/** Strokes land slightly early — a child rarely stops exactly on the end point. */
const COMPLETE_RATIO = 0.92;

/**
 * Walks the word's strokes in order. Progress only ever moves forward along the current stroke, and
 * wandering off it stalls rather than undoing anything.
 */
export const useGuidedTrace = (text: string, box: Box | null) => {
  const [layout, setLayout] = useState<TraceLayout | null>(null);
  const [completed, setCompleted] = useState(0);
  const [progress, setProgress] = useState(0);
  const [offset, setOffset] = useState(0);
  const [fontReady, setFontReady] = useState(false);

  // Pointer moves are batched, so the refs — not the state — are what the handlers may trust.
  const completedRef = useRef(0);
  const progressRef = useRef(0);
  const tracking = useRef(false);

  // Measuring before Andika loads would place every stroke against the fallback face.
  useEffect(() => {
    let active = true;
    document.fonts.ready.then(() => {
      if (active) setFontReady(true);
    });
    return () => {
      active = false;
    };
  }, []);

  const width = box?.width ?? 0;
  const height = box?.height ?? 0;

  useEffect(() => {
    completedRef.current = 0;
    progressRef.current = 0;
    tracking.current = false;
    setCompleted(0);
    setProgress(0);
    setOffset(0);
    setLayout(fontReady && text && width && height ? layoutWord(text, { width, height }) : null);
  }, [text, width, height, fontReady]);

  const strokes = layout?.strokes ?? [];
  const fontSize = layout?.fontSize ?? 0;
  const tolerance = Math.max(fontSize * TOLERANCE_EM, MIN_TOLERANCE);

  const currentStroke = strokes[completed] ?? null;
  const pen = currentStroke ? pointAt(currentStroke, progress) : null;

  // Re-centres per letter, never mid-stroke: moving the word under a finger would move the path too.
  const letter = currentStroke ? layout?.ink[currentStroke.charIndex] : null;
  useEffect(() => {
    if (!layout || !letter) return;
    setOffset(traceScrollOffset(letter, width, layout.contentWidth));
  }, [layout, letter, width]);

  const finishStroke = () => {
    completedRef.current += 1;
    progressRef.current = 0;
    tracking.current = false;
    setCompleted(completedRef.current);
    setProgress(0);
  };

  const start = (point: Point) => {
    const stroke = strokes[completedRef.current];
    if (!stroke) return;

    const from = pointAt(stroke, progressRef.current);
    if (Math.hypot(point.x - from.x, point.y - from.y) > tolerance * 1.4) return;

    tracking.current = true;
    // A tap has no path to follow, so landing on it is the whole stroke.
    if (!strokeLength(stroke)) finishStroke();
  };

  const move = (point: Point) => {
    const stroke = strokes[completedRef.current];
    if (!tracking.current || !stroke) return;

    const total = strokeLength(stroke);
    if (!total) return;

    const reached = advanceAlong(
      stroke,
      point,
      progressRef.current,
      fontSize * WINDOW_EM,
      tolerance
    );
    if (reached === null) return;

    if (reached >= total * COMPLETE_RATIO) {
      finishStroke();
      return;
    }
    progressRef.current = reached;
    setProgress(reached);
  };

  const end = () => {
    tracking.current = false;
  };

  return {
    layout,
    strokes,
    completed,
    progress,
    currentStroke,
    pen,
    tolerance,
    offset,
    done: layout !== null && completed >= strokes.length,
    start,
    move,
    end,
  };
};
